import { FieldLabel } from "../AdminCommon";
import { DELIVERY_CALENDAR_THEME, deliveryModalTitle } from "./deliveryCalendar";
import { DeliveryEhpadPicker } from "./DeliveryEhpadPicker";
import {
    deliveryCancelButtonStyle,
    deliveryDateGridStyle,
    deliveryDeleteButtonStyle,
    deliveryInputStyle,
    deliveryLabelStyle,
    deliveryModalBodyStyle,
    deliveryModalCloseButtonStyle,
    deliveryModalFooterStyle,
    deliveryModalFormStyle,
    deliveryModalHeaderStyle,
    deliveryModalStyle,
    deliveryModalSubtitleStyle,
    deliveryModalTitleStyle,
    deliverySectionTitleStyle,
    deliverySegmentButtonStyle,
    deliverySegmentStyle,
    deliverySubmitButtonStyle,
} from "../styles/adminDashboardFormStyles";
import { errorStyle, fieldStyle, modalBackdropStyle } from "../styles/adminDashboardStyles";
import ModalBackdrop from "../../../components/ModalBackdrop";

// Ponctuelle ou récurrente, date et heure de la première livraison, périodicité en jours.
function DeliveryScheduleSection({ delivery }) {
    const { form, setForm, setPlanningMode, handleFrequencyDaysChange } = delivery;
    const exceptional = form.frequency === "PONCTUELLE";
    return (
        <section style={{ display: "grid", gap: "18px" }}>
            <h3 style={deliverySectionTitleStyle}>Planification</h3>
            <div role="group" aria-label="Type de planification" style={deliverySegmentStyle}>
                <button type="button" aria-pressed={exceptional} onClick={() => setPlanningMode("PONCTUELLE")} style={deliverySegmentButtonStyle(exceptional)}>Ponctuelle</button>
                <button type="button" aria-pressed={!exceptional} onClick={() => setPlanningMode("HEBDOMADAIRE")} style={deliverySegmentButtonStyle(!exceptional)}>Récurrente</button>
            </div>

            <div style={deliveryDateGridStyle}>
                <div style={fieldStyle}>
                    <FieldLabel htmlFor="admin-delivery-date" required style={deliveryLabelStyle}>Date de la première livraison</FieldLabel>
                    <input id="admin-delivery-date" aria-label="Première livraison" type="date" style={deliveryInputStyle} value={form.firstDeliveryDate} onChange={e => setForm(f => ({ ...f, firstDeliveryDate: e.target.value }))} required />
                </div>
                <div style={fieldStyle}>
                    <FieldLabel htmlFor="admin-delivery-time" style={deliveryLabelStyle}>Heure</FieldLabel>
                    <input id="admin-delivery-time" type="time" style={deliveryInputStyle} value={form.deliveryTime} onChange={e => setForm(f => ({ ...f, deliveryTime: e.target.value }))} />
                </div>
            </div>

            {!exceptional && (
                <div style={fieldStyle}>
                    <FieldLabel htmlFor="admin-delivery-frequency-days" style={deliveryLabelStyle}>Périodicité (jours)</FieldLabel>
                    <input
                        id="admin-delivery-frequency-days"
                        type="number"
                        min="1"
                        step="1"
                        style={{ ...deliveryInputStyle, maxWidth: "260px" }}
                        value={form.frequencyDays}
                        onChange={e => handleFrequencyDaysChange(e.target.value)}
                    />
                </div>
            )}
        </section>
    );
}

function DeliveryDriverField({ value, drivers, onChange }) {
    return (
        <div style={fieldStyle}>
            <FieldLabel htmlFor="admin-delivery-driver" style={deliveryLabelStyle}>Livreur</FieldLabel>
            <select id="admin-delivery-driver" style={deliveryInputStyle} value={value} onChange={e => onChange(e.target.value)}>
                <option value="">Aucun livreur</option>
                {drivers.map(driver => {
                    const name = [driver.firstName, driver.lastName].filter(Boolean).join(" ").trim();
                    return (
                        <option key={driver.id} value={driver.id}>
                            {name || driver.email}
                        </option>
                    );
                })}
            </select>
        </div>
    );
}

function DeliveryModalFooter({ delivery, loading }) {
    const { editingId, submitting, resetForm, handleDelete } = delivery;
    return (
        <div style={deliveryModalFooterStyle}>
            <span style={{ color: DELIVERY_CALENDAR_THEME.gray, fontSize: "15px" }}>Les champs marqués * sont obligatoires.</span>
            <div style={{ display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                {editingId && (
                    <button type="button" onClick={() => handleDelete(editingId)} disabled={submitting} style={deliveryDeleteButtonStyle}>
                        Supprimer
                    </button>
                )}
                <button type="button" onClick={resetForm} disabled={submitting} style={deliveryCancelButtonStyle}>
                    Annuler
                </button>
                <button type="submit" disabled={submitting || loading} style={deliverySubmitButtonStyle}>
                    {submitting ? "Enregistrement..." : "Enregistrer la livraison"}
                </button>
            </div>
        </div>
    );
}

// Modale de création ou d'édition d'une livraison ; un clic hors de la modale la ferme, sauf pendant l'enregistrement.
export function DeliveryPlanningModal({ delivery, ehpads, drivers, loading }) {
    const { form, setForm, editingId, modalKind, submitting, formError, resetForm, handleSubmit } = delivery;
    const title = deliveryModalTitle(editingId, modalKind);
    return (
        <div style={modalBackdropStyle}>
            <ModalBackdrop onClose={() => !submitting && resetForm()} />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                style={{ ...deliveryModalStyle, position: "relative", zIndex: 1 }}
            >
                <form onSubmit={handleSubmit} style={deliveryModalFormStyle}>
                    <div style={deliveryModalHeaderStyle}>
                        <div>
                            <h2 style={deliveryModalTitleStyle}>{title}</h2>
                            <p style={deliveryModalSubtitleStyle}>Planifiez une tournée ponctuelle ou récurrente vers un ou plusieurs EHPADs.</p>
                        </div>
                        <button type="button" onClick={resetForm} disabled={submitting} aria-label="Fermer" style={deliveryModalCloseButtonStyle}>×</button>
                    </div>

                    <div style={deliveryModalBodyStyle}>
                        <div style={fieldStyle}>
                            <FieldLabel htmlFor="admin-delivery-label" required style={deliveryLabelStyle}>Nom de la livraison</FieldLabel>
                            <input id="admin-delivery-label" style={deliveryInputStyle} placeholder="Livraison matin" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} required />
                        </div>

                        <div style={fieldStyle}>
                            <FieldLabel htmlFor="admin-delivery-notes" style={deliveryLabelStyle}>Consignes</FieldLabel>
                            <textarea id="admin-delivery-notes" style={{ ...deliveryInputStyle, minHeight: "112px", resize: "vertical", lineHeight: 1.45 }} placeholder="Entrée, étage, contact..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                        </div>

                        <DeliveryScheduleSection delivery={delivery} />
                        <DeliveryDriverField value={form.driverId} drivers={drivers} onChange={driverId => setForm(f => ({ ...f, driverId }))} />
                        <DeliveryEhpadPicker delivery={delivery} ehpads={ehpads} loading={loading} />

                        {formError && <p style={errorStyle}>{formError}</p>}
                    </div>

                    <DeliveryModalFooter delivery={delivery} loading={loading} />
                </form>
            </div>
        </div>
    );
}
